/**
 * AdAnalyticsDashboard - 고급 광고 분석 대시보드
 * 
 * Features:
 * - 시간별 CTR 추세 (14/30일)
 * - 태그/지역/placement별 필터
 * - 비노출 광고 탐지 경고
 */

import { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { Advertisement, AdLocation } from "@/types/advertisement";
import { AlertTriangle, TrendingUp, Eye, MousePointerClick } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toArray, toNumber, calculateCTR } from "@/lib/array";

interface AdAnalyticsDashboardProps {
  ads: Advertisement[];
}

export function AdAnalyticsDashboard({ ads }: AdAnalyticsDashboardProps) {
  const [periodFilter, setPeriodFilter] = useState<'14' | '30'>('14');
  const [tagFilter, setTagFilter] = useState<string>('all');
  const [regionFilter, setRegionFilter] = useState<string>('all');
  const [placementFilter, setPlacementFilter] = useState<string>('all');

  // Safety: Ensure ads is an array
  const safeAds = toArray<Advertisement>(ads);

  // Extract unique tags, regions, placements
  const { tags, regions, placements } = useMemo(() => {
    const tagSet = new Set<string>();
    const regionSet = new Set<string>();
    const placementSet = new Set<string>();

    safeAds.forEach(ad => {
      if (ad.tags && Array.isArray(ad.tags)) {
        ad.tags.forEach(tag => tagSet.add(tag));
      }
      if (ad.region) regionSet.add(ad.region);
      if (ad.location) placementSet.add(ad.location);
    });

    return {
      tags: Array.from(tagSet).sort(),
      regions: Array.from(regionSet).sort(),
      placements: Array.from(placementSet).sort(),
    };
  }, [safeAds]);

  // Filter ads
  const filteredAds = useMemo(() => {
    return safeAds.filter(ad => {
      if (tagFilter !== 'all' && (!ad.tags || !ad.tags.includes(tagFilter))) return false;
      if (regionFilter !== 'all' && ad.region !== regionFilter) return false;
      if (placementFilter !== 'all' && ad.location !== placementFilter) return false;
      return true;
    });
  }, [safeAds, tagFilter, regionFilter, placementFilter]);

  // Detect zero-impression ads (비노출 광고)
  const zeroImpressionAds = useMemo(() => {
    return filteredAds.filter(ad => {
      const views = parseInt(ad.views || "0");
      return ad.status === "active" && views === 0;
    });
  }, [filteredAds]);

  // Calculate stats (with safety)
  const stats = useMemo(() => {
    const totalViews = filteredAds.reduce((sum, ad) => sum + toNumber(ad.views), 0);
    const totalClicks = filteredAds.reduce((sum, ad) => sum + toNumber(ad.clicks), 0);
    const avgCTR = calculateCTR(totalClicks, totalViews);

    return {
      totalViews,
      totalClicks,
      avgCTR,
      activeAds: filteredAds.filter(ad => ad.status === "active").length,
      totalAds: filteredAds.length,
    };
  }, [filteredAds]);

  // Simulated time-series data (CTR trend)
  // In real app, this would come from analytics API with daily data
  const trendData = useMemo(() => {
    const days = periodFilter === '14' ? 14 : 30;
    const data: any[] = [];
    
    // Simulate daily CTR trend (mock data)
    for (let i = days - 1; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      
      // Mock calculation: use current CTR with some variance
      const baseCTR = stats.avgCTR;
      const variance = (Math.random() - 0.5) * baseCTR * 0.3;
      const ctr = Math.max(0, baseCTR + variance);
      
      data.push({
        date: `${date.getMonth() + 1}/${date.getDate()}`,
        ctr: parseFloat(ctr.toFixed(2)),
        views: Math.floor(stats.totalViews / days + (Math.random() - 0.5) * 100),
        clicks: Math.floor(stats.totalClicks / days + (Math.random() - 0.5) * 10),
      });
    }
    
    return data;
  }, [periodFilter, stats]);

  // Performance by placement
  const placementData = useMemo(() => {
    const grouped: Record<string, { views: number; clicks: number; count: number }> = {};
    
    filteredAds.forEach(ad => {
      const placement = ad.location || 'unknown';
      if (!grouped[placement]) {
        grouped[placement] = { views: 0, clicks: 0, count: 0 };
      }
      grouped[placement].views += parseInt(ad.views || "0");
      grouped[placement].clicks += parseInt(ad.clicks || "0");
      grouped[placement].count++;
    });

    return Object.entries(grouped).map(([name, data]) => ({
      name,
      views: data.views,
      clicks: data.clicks,
      ctr: data.views > 0 ? parseFloat(((data.clicks / data.views) * 100).toFixed(2)) : 0,
      ads: data.count,
    }));
  }, [filteredAds]);

  return (
    <div className="space-y-6" data-testid="ad-analytics-dashboard">
      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>필터 & 분석 설정</CardTitle>
          <CardDescription>태그, 지역, 배치 위치별로 광고 성과를 분석하세요</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">기간</label>
              <Select value={periodFilter} onValueChange={(v: any) => setPeriodFilter(v)}>
                <SelectTrigger data-testid="select-period">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="14">최근 14일</SelectItem>
                  <SelectItem value="30">최근 30일</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">태그</label>
              <Select value={tagFilter} onValueChange={setTagFilter}>
                <SelectTrigger data-testid="select-tag">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">전체</SelectItem>
                  {tags.map(tag => (
                    <SelectItem key={tag} value={tag}>{tag}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">지역</label>
              <Select value={regionFilter} onValueChange={setRegionFilter}>
                <SelectTrigger data-testid="select-region">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">전체</SelectItem>
                  {regions.map(region => (
                    <SelectItem key={region} value={region}>{region}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">배치 위치</label>
              <Select value={placementFilter} onValueChange={setPlacementFilter}>
                <SelectTrigger data-testid="select-placement">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">전체</SelectItem>
                  {placements.map(placement => (
                    <SelectItem key={placement} value={placement}>{placement}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Active filters display */}
          <div className="flex gap-2 mt-4">
            {tagFilter !== 'all' && (
              <Badge variant="secondary">태그: {tagFilter}</Badge>
            )}
            {regionFilter !== 'all' && (
              <Badge variant="secondary">지역: {regionFilter}</Badge>
            )}
            {placementFilter !== 'all' && (
              <Badge variant="secondary">배치: {placementFilter}</Badge>
            )}
            {(tagFilter !== 'all' || regionFilter !== 'all' || placementFilter !== 'all') && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => {
                  setTagFilter('all');
                  setRegionFilter('all');
                  setPlacementFilter('all');
                }}
                data-testid="button-clear-filters"
              >
                필터 초기화
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Zero Impression Alert */}
      {zeroImpressionAds.length > 0 && (
        <Alert variant="destructive" data-testid="alert-zero-impressions">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            <div className="font-semibold mb-2">
              ⚠️ {zeroImpressionAds.length}개 광고가 노출되지 않고 있습니다
            </div>
            <div className="text-sm">
              다음 광고들의 설정을 확인하세요: {zeroImpressionAds.slice(0, 3).map(ad => ad.advertiser).join(', ')}
              {zeroImpressionAds.length > 3 && ` 외 ${zeroImpressionAds.length - 3}개`}
            </div>
          </AlertDescription>
        </Alert>
      )}

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Eye className="h-4 w-4 text-muted-foreground" />
              총 노출
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold" data-testid="stat-total-views">
              {stats.totalViews.toLocaleString()}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <MousePointerClick className="h-4 w-4 text-muted-foreground" />
              총 클릭
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold" data-testid="stat-total-clicks">
              {stats.totalClicks.toLocaleString()}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
              평균 CTR
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold" data-testid="stat-avg-ctr">
              {stats.avgCTR}%
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">활성 광고</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold" data-testid="stat-active-ads">
              {stats.activeAds} / {stats.totalAds}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* CTR Trend Chart */}
      <Card>
        <CardHeader>
          <CardTitle>CTR 추세 ({periodFilter}일)</CardTitle>
          <CardDescription>일별 클릭률 변화 추이</CardDescription>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={trendData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis yAxisId="left" />
              <YAxis yAxisId="right" orientation="right" />
              <Tooltip />
              <Legend />
              <Line yAxisId="left" type="monotone" dataKey="ctr" stroke="#8884d8" name="CTR %" strokeWidth={2} />
              <Line yAxisId="right" type="monotone" dataKey="clicks" stroke="#82ca9d" name="Clicks" />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Performance by Placement */}
      <Card>
        <CardHeader>
          <CardTitle>배치 위치별 성과</CardTitle>
          <CardDescription>광고 위치에 따른 노출 및 클릭 비교</CardDescription>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={placementData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="views" fill="#8884d8" name="Views" />
              <Bar dataKey="clicks" fill="#82ca9d" name="Clicks" />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Placement Details Table */}
      <Card>
        <CardHeader>
          <CardTitle>배치 상세 통계</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {placementData.map((placement, index) => (
              <div 
                key={index} 
                className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
                data-testid={`placement-stat-${index}`}
              >
                <div>
                  <div className="font-medium">{placement.name}</div>
                  <div className="text-sm text-muted-foreground">
                    {placement.ads}개 광고
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-bold">{placement.ctr}% CTR</div>
                  <div className="text-sm text-muted-foreground">
                    {placement.views.toLocaleString()} views · {placement.clicks.toLocaleString()} clicks
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default AdAnalyticsDashboard;
